import { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { readData } from '@/firebase/database';

interface Skill {
  name: string;
  level: number;
  category: string;
}

const SkillBar = ({ skill, index }: { skill: Skill; index: number }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }} className="glass neon-border rounded-xl p-3 sm:p-4 card-hover">
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <span className="font-medium text-foreground text-xs sm:text-sm">{skill.name}</span>
        <span className="text-primary font-mono text-xs sm:text-sm font-bold">{skill.level}%</span>
      </div>
      <div className="h-1 sm:h-1.5 bg-muted rounded-full overflow-hidden">
        <motion.div className="skill-bar-fill" initial={{ width: 0 }}
          animate={inView ? { width: `${skill.level}%` } : {}}
          transition={{ duration: 1.5, delay: index * 0.1 + 0.3, ease: 'easeOut' }} />
      </div>
      <div className="mt-1.5 sm:mt-2">
        <span className="text-[10px] sm:text-xs text-muted-foreground font-mono px-2 py-0.5 rounded-full border border-border">{skill.category}</span>
      </div>
    </motion.div>
  );
};

const Skills = () => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [filter, setFilter] = useState('All');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    readData('skills').then((data) => {
      if (data) setSkills(Object.values(data) as Skill[]);
    });
  }, []);

  const categories = ['All', ...Array.from(new Set(skills.map((s) => s.category)))];
  const filtered = filter === 'All' ? skills : skills.filter((s) => s.category === filter);

  return (
    <section id="skills" className="py-16 sm:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-10 sm:mb-16">
          <p className="section-tag text-xs">What I work with</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
            Tech <span className="gradient-text">Stack</span>
          </h2>
          <div className="w-20 h-0.5 mx-auto mt-4 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap gap-2 justify-center mb-8 sm:mb-10">
          {categories.map((cat) => (
            <button key={cat} onClick={() => setFilter(cat)}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-mono transition-all duration-300 ${
                filter === cat ? 'bg-primary text-primary-foreground font-bold' : 'glass border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}>{cat}</button>
          ))}
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {filtered.map((skill, i) => <SkillBar key={skill.name} skill={skill} index={i} />)}
        </div>
      </div>
    </section>
  );
};

export default Skills;
